import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Video, FileText, Briefcase, Beaker, Award, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface PendingItemsData {
  pendingContent: number;
  pendingDeliverables: number;
  hasSubmittedProject: boolean;
  challengeLabCompleted: boolean;
  /** When set, shows a "Certificado disponible" highlight that links to the profile */
  certificateAvailable?: boolean;
  /** Self-paced route mode: hides deliverables/final project rows */
  selfPaced?: boolean;
  /** Number of mandatory contents past their suggested date (self-paced) */
  overdueContent?: number;
}

export type PendingSection = 'content' | 'deliverables';

interface PendingItemsWidgetProps {
  data: PendingItemsData;
  /** Called when user clicks a row that should open a filtered detail modal */
  onShowSection?: (section: PendingSection) => void;
  /** Compact variant for sidebar usage */
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Standardized "Pendientes" widget shared between /dashboard and /calendar.
 * Single source of truth so both views always show identical info.
 */
export function PendingItemsWidget({
  data,
  onShowSection,
  variant = 'default',
  className,
}: PendingItemsWidgetProps) {
  const navigate = useNavigate();
  const { pendingContent, pendingDeliverables, hasSubmittedProject, challengeLabCompleted, certificateAvailable, selfPaced, overdueContent = 0 } = data;

  const isCompact = variant === 'compact';
  const rowPadding = isCompact ? 'p-2' : 'p-3';
  const textSize = isCompact ? 'text-xs' : 'text-sm';
  const iconSize = isCompact ? 'w-4 h-4' : 'w-5 h-5';
  const titleSize = isCompact ? 'text-base' : 'text-lg';

  const contentClickable = pendingContent > 0 && !!onShowSection;
  const deliverablesClickable = pendingDeliverables > 0 && !!onShowSection;

  return (
    <Card className={cn('glass-card', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className={cn(iconSize, 'text-warning')} />
          <CardTitle className={cn(titleSize, 'font-display')}>Pendientes</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {certificateAvailable && !selfPaced && (
          <button
            type="button"
            onClick={() => navigate('/profile?tab=perfil')}
            className={cn(
              'mb-3 w-full flex items-center justify-between rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-left transition-colors hover:bg-success/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Award className={cn(iconSize, 'text-success')} />
              <span className={cn(textSize, 'font-semibold text-success')}>🏆 Certificado disponible</span>
            </div>
            <span className="text-xs text-success">Ver →</span>
          </button>
        )}
        <div className={cn('space-y-3', isCompact && 'space-y-2')}>
          {/* Contenido */}
          <button
            type="button"
            disabled={!contentClickable}
            onClick={() => contentClickable && onShowSection?.('content')}
            className={cn(
              'w-full flex items-center justify-between rounded-lg bg-muted/50 transition-colors text-left',
              rowPadding,
              contentClickable && 'hover:bg-muted cursor-pointer',
              !contentClickable && 'cursor-default',
            )}
          >
            <div className="flex items-center gap-3">
              <Video className={cn(iconSize, 'text-primary')} />
              <span className={cn(textSize, 'text-foreground')}>
                Contenido obligatorio
                {selfPaced && overdueContent > 0 && (
                  <span className="ml-1 text-warning font-semibold">— {overdueContent} vencidos</span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  textSize,
                  'font-semibold',
                  pendingContent > 0 ? 'text-warning' : 'text-success',
                )}
              >
                {pendingContent > 0 ? `${pendingContent} pendientes` : '✓ Completo'}
              </span>
              {contentClickable && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          </button>

          {/* Entregables */}
          {!selfPaced && (
          <button
            type="button"
            disabled={!deliverablesClickable}
            onClick={() => deliverablesClickable && onShowSection?.('deliverables')}
            className={cn(
              'w-full flex items-center justify-between rounded-lg bg-muted/50 transition-colors text-left',
              rowPadding,
              deliverablesClickable && 'hover:bg-muted cursor-pointer',
              !deliverablesClickable && 'cursor-default',
            )}
          >
            <div className="flex items-center gap-3">
              <FileText className={cn(iconSize, 'text-primary')} />
              <span className={cn(textSize, 'text-foreground')}>Entregables</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  textSize,
                  'font-semibold',
                  pendingDeliverables > 0 ? 'text-warning' : 'text-success',
                )}
              >
                {pendingDeliverables > 0 ? `${pendingDeliverables} pendientes` : '✓ Completo'}
              </span>
              {deliverablesClickable && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          </button>
          )}

          {/* Proyecto Final */}
          {!selfPaced && (
          <div className={cn('flex items-center justify-between rounded-lg bg-muted/50', rowPadding)}>
            <div className="flex items-center gap-3">
              <Briefcase className={cn(iconSize, 'text-primary')} />
              <span className={cn(textSize, 'text-foreground')}>Proyecto Final</span>
            </div>
            {hasSubmittedProject ? (
              <span className={cn(textSize, 'font-semibold text-success')}>✓ Enviado</span>
            ) : (
              <Button
                variant="link"
                size="sm"
                className={cn('text-warning font-semibold p-0 h-auto', textSize)}
                onClick={() => navigate('/challenge')}
              >
                Pendiente →
              </Button>
            )}
          </div>
          )}

          {/* Lab Retos */}
          {!selfPaced && (
          <div className={cn('flex items-center justify-between rounded-lg bg-muted/50', rowPadding)}>
            <div className="flex items-center gap-3">
              <Beaker className={cn(iconSize, 'text-primary')} />
              <span className={cn(textSize, 'text-foreground')}>Lab. Retos y Soluciones</span>
            </div>
            {challengeLabCompleted ? (
              <span className={cn(textSize, 'font-semibold text-success')}>✓ Completo</span>
            ) : (
              <Button
                variant="link"
                size="sm"
                className={cn('text-warning font-semibold p-0 h-auto', textSize)}
                onClick={() => navigate('/challenge-lab')}
              >
                Pendiente →
              </Button>
            )}
          </div>
          )}

          {selfPaced && pendingContent === 0 && (
            <div className={cn('flex items-center justify-center rounded-lg bg-success/10', rowPadding)}>
              <span className={cn(textSize, 'font-semibold text-success')}>✓ Vas al día con tu ruta</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
